import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Prompt } from "react-router-dom";
import { isEmpty, groupBy, cloneDeep } from "lodash";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";

import CallService from "../../services/callService";
import * as callActions from "../../actions/callActions";
import * as modalActions from "../../actions/modalActions";
import { customToast as toast } from "../../utils";
import CallApprovalList from "./CallApprovalList";
import Checkbox from "../common/Checkbox";
import Badge from "../common/Badge";

const Card = styled.div`
  display: grid;
  grid-template-areas: "checkbox info";
  grid-template-columns: auto 4fr;
  grid-gap: 25px;
  align-items: center;
  padding: 15px 5px;
  font-family: "Open Sans";
  border-bottom: ${(props) => (!props.open ? "1px solid #efefef" : "none")};
  strong {
    margin: 0;
    letter-spacing: 0 !important;
    grid-area: name;
  }
  .badge {
    margin: 0 auto;
    width: 80px;
    &.success {
      grid-area: badge;
      background: ${(props) => props.theme.green_g} !important;
    }
    &.danger {
      background: ${(props) => props.theme.red_g};
      grid-area: flagged;
    }
  }
  svg {
    grid-area: icon;
  }
  .footer-totals {
    grid-area: totals;
    display: flex;
    justify-content: space-around;
    p {
      font-family: "Open Sans";
      margin-bottom: 0;
    }
  }
`;

const Info = styled.div`
  display: grid;
  grid-template-areas: "name badge flagged totals icon";
  grid-template-columns: 1fr 1fr 1fr 4fr auto;
  grid-gap: 15px;
  align-items: center;
  padding: 15px 5px;
  cursor: pointer;
`;

const CallApprovalContainer = ({
  reviewType,
  codes,
  auth,
  contracts,
  calls,
  projects,
  services,
  actions,
  customers,
  users,
  openEmailNotification,
  filters,
  loadCalls
}) => {
  const [open, setOpen] = useState(false);
  const [needUpdate, setNeedUpdate] = useState(false);
  const [stateCalls, setStateCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [sortBy, setSortBy] = useState({});

  const [highLevelApprove] = useState(
    reviewType === "Invoice" || reviewType === "Billing"
  );
  const [billableBreakdown, setBillableBreakdown] = useState(null);
  const groupedByCustomer = useMemo(
    () => !!filters.grouped_by_customer,
    [filters]
  );

  useEffect(() => {
    if (Array.isArray(calls)) {
      const callMap = new Map();

      calls.forEach((call) => callMap.set(call._id, call));

      stateCalls.forEach((call) => callMap.set(call._id, call));

      setStateCalls(Array.from(callMap.values()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- No need to rerun when state calls are updated
  }, [calls]);

  useEffect(() => {
    if (Object.keys(sortBy).length) {
      const objects = [
        "projectObj",
        "serviceObj",
        "callType",
        "customerObj",
        "user"
      ];
      const [columnToSort] = Object.keys(sortBy);
      let sortedCalls = stateCalls;

      if (objects.includes(columnToSort)) {
        sortedCalls = stateCalls.sort((a, b) => {
          let accessor = a?.[columnToSort]?.description
            ? "description"
            : "name";
          if (!a?.[columnToSort].name && !a?.[columnToSort]?.description) {
            accessor = columnToSort === "user" ? "full_name" : "label";
          }

          if (
            a?.[columnToSort]?.[accessor] > b?.[columnToSort]?.[accessor] &&
            sortBy[columnToSort] === "desc"
          ) {
            return -1;
          }
          if (
            b?.[columnToSort]?.[accessor] > a?.[columnToSort]?.[accessor] &&
            sortBy[columnToSort] === "desc"
          ) {
            return 1;
          }

          if (
            a?.[columnToSort]?.[accessor] > b?.[columnToSort]?.[accessor] &&
            sortBy[columnToSort] === "asc"
          ) {
            return 1;
          }
          if (
            b?.[columnToSort]?.[accessor] > a?.[columnToSort]?.[accessor] &&
            sortBy[columnToSort] === "asc"
          ) {
            return -1;
          }
          return 0;
        });
      } else if (columnToSort === "arrival_date") {
        sortedCalls = stateCalls.sort((a, b) =>
          sortBy[columnToSort] === "asc"
            ? new Date(a?.[columnToSort]).getTime() -
              new Date(b?.[columnToSort]).getTime()
            : new Date(b?.[columnToSort]).getTime() -
              new Date(a?.[columnToSort]).getTime()
        );
      } else {
        sortedCalls = stateCalls.sort((a, b) =>
          sortBy[columnToSort] === "asc"
            ? a?.[columnToSort] - b?.[columnToSort]
            : b?.[columnToSort] - a?.[columnToSort]
        );
      }
      setStateCalls(sortedCalls);
    }
  }, [sortBy, stateCalls]);

  useEffect(() => {
    if (
      !billableBreakdown &&
      highLevelApprove &&
      Array.isArray(stateCalls) &&
      stateCalls.length
    ) {
      setLoading(true);
      const callsFiltered = stateCalls;
      const groupedCalls = groupBy(callsFiltered, "contract");
      const newBillableBreakdown = [];
      for (const [key, value] of Object.entries(groupedCalls)) {
        let hours = 0;
        let color = "#262626";
        const contractType =
          key &&
          codes.find(
            (c) =>
              c._id.toString() ===
              value?.[0]?.contractObj?.contract_type.toString()
          );
        if (contractType?.label === "BILLABLE") {
          color = "red";
        }
        const contractName = value[0]?.contractObj?.name || "No Contract";
        value.map((call) => (hours += call.billable_hrs || 0));
        newBillableBreakdown.push({ contract: contractName, hours, color });
      }
      setBillableBreakdown(newBillableBreakdown);
      setLoading(false);
    }
  }, [
    actions,
    billableBreakdown,
    calls,
    codes,
    highLevelApprove,
    setBillableBreakdown,
    stateCalls
  ]);

  const handleCallsSave = async (callsToUpdate) => {
    try {
      await CallService.updateManyCalls(callsToUpdate);
    } catch (err) {
      toast.error();
    }
  };
  const onClickSave = async () => {
    setLoading(true);
    const callsToUpdate = cloneDeep(stateCalls).filter(
      (call) =>
        call.customer_changed ||
        call.is_flagged_changed ||
        call.approved_changed ||
        call.fixed_time_changed ||
        call.off_hours_changed ||
        call.service_changed ||
        call.internal_notes_changed ||
        call.billing_description_changed ||
        call.review_notes_changed ||
        call.completed_date_changed ||
        call.project_changed ||
        call.contract_changed ||
        call.billable_hrs_changed ||
        call.call_type_changed ||
        call.invoice_notes_changed ||
        call.tasks_changed
    );
    if (callsToUpdate && callsToUpdate.length > 0) {
      const formatedCalls = callsToUpdate.map((call) => {
        const project = projects.find((p) => p._id === call.project);
        call.contract = project.contract;
        call.recurring_id = null;
        call.modified_by = auth.user.email;
        if (call.contract === undefined) {
          call.contract = null;
        }
        delete call.customer_changed;
        delete call.is_flagged_changed;
        if (call.approved_changed) {
          delete call.approved_changed;
          delete call.approved;
        }
        delete call.fixed_time_changed;
        delete call.off_hours_changed;
        delete call.service_changed;
        delete call.internal_notes_changed;
        delete call.billing_description_changed;
        delete call.review_notes_changed;
        delete call.completed_date_changed;
        delete call.project_changed;
        delete call.contract_changed;
        delete call.billable_hrs_changed;
        delete call.call_type_changed;
        delete call.invoice_notes_changed;
        delete call.tasks_changed;
        if (call.contract && call.contract.length === 0) {
          delete call.contract;
        }
        return call;
      });

      setNeedUpdate(false);
      await handleCallsSave(formatedCalls);
      await loadCalls(false);
    }

    toast.success("Changes saved successfully.");
    setLoading(false);
  };

  const onClickSaveAndApprove = async () => {
    try {
      setLoading(true);
      let stageValue;
      if (reviewType === "Manager") {
        // Submitted
        stageValue = "71S";
      } else if (reviewType === "Finance") {
        // Ready to Invoice
        stageValue = "80R";
      } else if (reviewType === "Invoice") {
        // Invoiced (Exported)
        stageValue = "89E";
      } else if (reviewType === "Billing") {
        // Billed
        stageValue = "99N";
      }

      const stage = codes.find(
        (code) => code.entity === "Call" && code.value === stageValue
      );

      const callsToApprove = stateCalls
        .filter((call) => call.approved)
        .map((call) => call._id);

      await CallService.bulkApprove(callsToApprove, auth.user._id, stage._id);
      await onClickSave();
      const callsWithOutApproved = stateCalls.filter(
        (stateCall) => !callsToApprove.find((id) => id === stateCall._id)
      );

      await setStateCalls(callsWithOutApproved);
      setNeedUpdate(false);
    } catch (error) {
      toast.error(error);
    } finally {
      await setLoading(false);
    }
  };

  const updateCall = (callUpdated) => {
    setStateCalls((prev) =>
      prev.map((sCall) => (sCall._id === callUpdated._id ? callUpdated : sCall))
    );
  };

  const toggleApproveAll = async () => {
    await setLoading(true);
    setCheckAll(!checkAll);
    setOpen(open || true);
    const newCallsApproved = await Promise.all(
      stateCalls.map((call) =>
        !call.is_flagged
          ? { ...call, approved: !checkAll, approved_changed: true }
          : call
      )
    );
    setStateCalls(newCallsApproved);
    await setLoading(false);
  };

  const numFlagged = stateCalls
    ? stateCalls.filter(({ is_flagged }) => is_flagged).length
    : 0;

  const approvedList =
    stateCalls && Array.isArray(stateCalls) && stateCalls.length > 0
      ? stateCalls.filter((call) => call.approved && call.approved_changed)
      : [];

  const handleSortCalls = (column) => {
    setSortBy((prev) => {
      if (prev?.[column] === "asc") {
        return { [column]: "desc" };
      }
      return { [column]: "asc" };
    });
  };

  return (
    !isEmpty(calls) &&
    !isEmpty(stateCalls) && (
      <React.Fragment>
        <Prompt
          when={needUpdate}
          message={() =>
            `Are you sure you want to leave this page? Changes you made may not be saved`
          }
        />
        <Card open={open} className="car-call">
          <Checkbox
            style={{ margin: "0 auto" }}
            checked={checkAll}
            name="all-checked"
            onClickCheck={toggleApproveAll}
          />
          <Info
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}
          >
            <p style={{ width: 155 }}>
              {groupedByCustomer
                ? calls[0].customerObj.name
                : calls[0].user.full_name}
            </p>
            <Badge className="badge success">
              {stateCalls.length} {stateCalls.length === 1 ? "Call" : "Calls"}
            </Badge>
            {numFlagged > 0 && (
              <Badge className="badge danger">{numFlagged} Flagged</Badge>
            )}
            <span className="footer-totals">
              <p style={{ marginRight: 10, fontWeight: 600 }}>
                {stateCalls.length
                  ? stateCalls
                      .reduce((total, call) => total + call.actual_hrs, 0)
                      .toFixed(2)
                  : "--"}{" "}
                Hours
              </p>
              <p style={{ marginRight: 10, fontWeight: 600 }}>
                {stateCalls.length
                  ? stateCalls
                      .reduce(
                        (total, call) => total + (call.billable_hrs || 0),
                        0
                      )
                      .toFixed(2)
                  : "--"}{" "}
                Hours
              </p>
              <p style={{ marginRight: 10, fontWeight: 600 }}>
                {stateCalls.length
                  ? stateCalls
                      .reduce(
                        (total, call) =>
                          call.billable_hrs <= 0
                            ? total + call.actual_hrs
                            : total,
                        0
                      )
                      .toFixed(2)
                  : "--"}{" "}
                Hours
              </p>
            </span>
            {open ? (
              <IoChevronUpOutline
                style={{ cursor: "pointer" }}
                size={20}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen((prev) => !prev);
                }}
              />
            ) : (
              <IoChevronDownOutline
                style={{ cursor: "pointer" }}
                size={20}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen((prev) => !prev);
                }}
              />
            )}
          </Info>
        </Card>
        {open && (
          <CallApprovalList
            {...{
              calls: stateCalls,
              loading,
              updateCall,
              auth,
              codes,
              projects,
              contracts,
              services,
              setNeedUpdate,
              highLevelApprove,
              onSortCalls: handleSortCalls,
              sortBy,
              customers,
              users,
              openEmailNotification,
              filters,
              reviewType
            }}
          />
        )}
        {highLevelApprove && open && (
          <div className="call-buttons">
            <h4 style={{ marginBottom: "5px", fontWeight: "bold" }}>
              Contract Billable Hours:
            </h4>
            {billableBreakdown &&
              billableBreakdown.map((contract, i) => (
                <div
                  key={i}
                  style={{ marginBottom: "5px", color: contract.color }}
                >
                  - {contract.contract}: <strong>{contract.hours}</strong>
                </div>
              ))}
          </div>
        )}
        {open && (
          <div className="call-buttons">
            <button
              className="btn btn-cancel"
              onClick={onClickSave}
              disabled={
                loading ||
                !needUpdate ||
                stateCalls.find(
                  (call) =>
                    (call.internal_notes &&
                      call.internal_notes.length > 1500) ||
                    (call.review_notes && call.review_notes.length > 500) ||
                    (call.billing_description &&
                      call.billing_description.length > 100) ||
                    (highLevelApprove &&
                      call.invoice_notes &&
                      call.invoice_notes.length > 500)
                )
              }
            >
              {`SAV${loading ? `ING` : `E`}`}
            </button>
            <button
              className="btn btn-success"
              disabled={
                approvedList.length === 0 ||
                loading ||
                stateCalls.find(
                  (call) =>
                    (call.internal_notes &&
                      call.internal_notes.length > 1500) ||
                    (call.review_notes && call.review_notes.length > 500) ||
                    (call.billing_description &&
                      call.billing_description.length > 100) ||
                    (highLevelApprove &&
                      call.invoice_notes &&
                      call.invoice_notes.length > 500)
                )
              }
              onClick={onClickSaveAndApprove}
            >
              {approvedList &&
                approvedList.length === 0 &&
                needUpdate &&
                "Save and Approve"}
              {approvedList &&
                approvedList.length === 0 &&
                !needUpdate &&
                "Approve"}
              {needUpdate &&
                approvedList &&
                approvedList.length > 0 &&
                `Save Changes and Approve ${approvedList.length} Call${
                  approvedList.length > 1 ? "s" : ""
                }`}
              {!needUpdate &&
                approvedList &&
                approvedList.length > 0 &&
                `Approve ${approvedList.length} Call${
                  approvedList.length > 1 ? "s" : ""
                }`}
            </button>
          </div>
        )}
      </React.Fragment>
    )
  );
};

CallApprovalContainer.propTypes = {
  customers: PropTypes.array,
  users: PropTypes.array,
  actions: PropTypes.object,
  callToSave: PropTypes.object,
  userId: PropTypes.string,
  codes: PropTypes.array,
  auth: PropTypes.object,
  contracts: PropTypes.array,
  filters: PropTypes.object,
  saveCall: PropTypes.func,
  calls: PropTypes.array,
  handleRefetch: PropTypes.func,
  projects: PropTypes.array,
  services: PropTypes.array,
  reviewType: PropTypes.string,
  loadCalls: PropTypes.func,
  openEmailNotification: PropTypes.func
};

function mapStatesToProps(state) {
  return {
    callToSave: state.reducers.callToSave
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...modalActions,
        ...callActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(CallApprovalContainer);
